import React, { useEffect, useState, useMemo } from 'react';
import { 
  IonApp, 
  IonRouterOutlet, 
  IonTabs,
  IonTabBar,
  IonTabButton,
  IonIcon,
  IonLabel,
  setupIonicReact 
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Route, Redirect, useLocation } from 'react-router-dom';
import { homeOutline, bookOutline, codeSlashOutline, playOutline, personOutline } from 'ionicons/icons';

// Pages
import Home from './pages/Home';
import Onboarding from './pages/Onboarding';
import LessonsList from './pages/LessonsList';
import LessonDetail from './pages/LessonDetail';
import Challenges from './pages/Challenges';
import ChallengeDetail from './pages/ChallengeDetail';
import CodePlayground from './pages/CodePlayground';
import Achievements from './pages/Achievements';
import InterviewQuestions from './pages/InterviewQuestions';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import About from './pages/About';
import Help from './pages/Help';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import IllustrationShowcase from './pages/IllustrationShowcase';

// Services
import { configService } from './services/ConfigService';
import { storageService } from './services/StorageService';
import { adService } from './services/AdService';
import { themeService } from './services/ThemeService';
import { streakService } from './services/StreakService';
import { seoService } from './services/SEOService';

// Ionic CSS
import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

// Theme
import './theme/variables.css';
import './theme/animations.css';
import './App.css';

setupIonicReact();

function App() {
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    async function initialize() {
      try {
        // Load all configs
        await configService.loadAll();
        const appConfig = configService.getConfig();
        setConfig(appConfig);
        
        // Initialize SEO Service with full config
        seoService.initialize(appConfig);
        
        // Update favicon to app-specific icon
        const iconPath = appConfig?.branding?.icon;
        if (iconPath) {
          const favicon = document.querySelector('link[rel="icon"]');
          if (favicon) {
            favicon.href = `/assets/media/${iconPath}`;
            favicon.type = 'image/svg+xml';
          }
        }
        
        // Update document title to app name
        if (appConfig?.app?.name) {
          document.title = appConfig.app.name;
        }
        
        // Apply theme colors from config
        themeService.applyConfigColors('light');
        
        // Apply saved dark mode preference from user_settings
        const settings = storageService.getSettings();
        if (settings.darkMode) {
          themeService.applyTheme('dark');
        } else {
          themeService.applyTheme('light');
        }
        
        // Update streak - record activity for today
        streakService.recordActivity();
        
        // Initialize ads if enabled (FREE version)
        if (configService.isAdsEnabled()) {
          await adService.initialize();
        }
        
        // Check onboarding
        const onboardingComplete = storageService.get('onboarding_complete');
        if (!onboardingComplete && appConfig.onboarding?.enabled) {
          setShowOnboarding(true);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Failed to initialize:', error);
        setLoading(false);
      }
    }
    
    initialize();
  }, []);

  const handleOnboardingComplete = () => {
    storageService.set('onboarding_complete', true);
    setShowOnboarding(false);
  };

  // Loading tips that rotate - generic messages work for all languages
  const loadingTips = useMemo(() => [
    { emoji: "⚡", text: "Compiling awesomeness..." },
    { emoji: "🎮", text: "Loading fun lessons..." },
    { emoji: "🚀", text: "Preparing your coding journey..." },
    { emoji: "✨", text: "Initializing magic..." },
    { emoji: "💻", text: "Setting up your workspace..." },
    { emoji: "🎯", text: "Getting everything perfect..." },
    { emoji: "📚", text: "Loading your lessons..." },
    { emoji: "🌟", text: "Almost there..." }
  ], []);

  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setTipIndex(prev => (prev + 1) % loadingTips.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [loading, loadingTips.length]);

  // Hide the HTML initial loader IMMEDIATELY when React mounts
  // This ensures seamless transition from HTML loader to React loader
  useEffect(() => {
    const initialLoader = document.getElementById('initial-loader');
    if (initialLoader) {
      // Add hidden class immediately for fade out
      initialLoader.classList.add('hidden');
      // Remove from DOM after transition completes
      setTimeout(() => {
        if (initialLoader.parentNode) {
          initialLoader.remove();
        }
      }, 300);
    }
  }, []); // Empty dependency = runs once on mount

  // Get mascot and app info from config (with fallbacks for initial load)
  const mascotName = config?.mascot?.name || 'Your Guide';
  const getMascotImage = () => {
    if (config?.mascot?.basePath && config?.mascot?.states?.idle) {
      return `/assets/media/${config.mascot.basePath}/${config.mascot.states.idle}`;
    }
    // Fallback to language-based path if config has language
    const language = config?.app?.language || 'python';
    return `/assets/media/mascots/${language}/mascot.svg`;
  };
  const mascotImage = getMascotImage();
  const appDisplayName = config?.app?.displayName || config?.app?.name || 'CodeLearn';
  const languageName = config?.app?.language 
    ? config.app.language.charAt(0).toUpperCase() + config.app.language.slice(1)
    : 'Coding';

  if (loading) {
    const currentTip = loadingTips[tipIndex];
    
    return (
      <IonApp>
        <div className="loading-screen">
          {/* Floating code symbols */}
          <div className="loading-particles">
            <span className="particle particle-1">{"{ }"}</span>
            <span className="particle particle-2">{"< />"}</span>
            <span className="particle particle-3">{"//"}</span>
            <span className="particle particle-4">{";"}</span>
            <span className="particle particle-5">{"=>"}</span>
            <span className="particle particle-6">{"( )"}</span>
            <span className="particle particle-7">{"[ ]"}</span>
            <span className="particle particle-8">{"#"}</span>
          </div>
          
          {/* Floating decorative icons */}
          <div className="loading-icons">
            <span className="floating-icon icon-1">📚</span>
            <span className="floating-icon icon-2">💻</span>
            <span className="floating-icon icon-3">⭐</span>
            <span className="floating-icon icon-4">🏆</span>
          </div>
          
          <div className="loading-content">
            {/* Mascot with glow effect */}
            <div className="loading-mascot-container">
              <div className="mascot-glow"></div>
              <img 
                src={mascotImage}
                alt={mascotName}
                className="loading-mascot"
              />
            </div>
            
            {/* App name - uses config */}
            <h1 className="loading-title">
              <span className="title-bracket">{"{"}</span>
              <span className="title-text">{appDisplayName}</span>
              <span className="title-bracket">{"}"}</span>
            </h1>
            
            {/* Subtitle with language */}
            <p className="loading-subtitle">Learn {languageName} the fun way!</p>
            
            {/* Loading message */}
            <div className="loading-message" key={tipIndex}>
              <span className="message-emoji">{currentTip.emoji}</span>
              <span className="message-text">{currentTip.text}</span>
            </div>
            
            {/* Code-themed progress bar */}
            <div className="loading-progress">
              <span className="progress-bracket">{"{"}</span>
              <div className="progress-track">
                <div className="progress-fill-animated"></div>
                <div className="progress-cursor"></div>
              </div>
              <span className="progress-bracket">{"}"}</span>
            </div>
            
            {/* Typing dots */}
            <div className="loading-dots">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          </div>
        </div>
      </IonApp>
    );
  }

  if (showOnboarding) {
    return (
      <IonApp>
        <Onboarding onComplete={handleOnboardingComplete} />
      </IonApp>
    );
  }

  return (
    <IonApp>
      <IonReactRouter>
        <TabsContainer />
      </IonReactRouter>
    </IonApp>
  );
}

// Separate component to use useLocation inside router context
function TabsContainer() {
  const location = useLocation();
  
  // Helper to check if tab is selected based on current path
  const isTabSelected = (tabPath) => {
    if (tabPath === '/home') {
      return location.pathname === '/home' || location.pathname === '/';
    }
    return location.pathname === tabPath || location.pathname.startsWith(tabPath + '/');
  };

  return (
    <IonTabs>
      <IonRouterOutlet>
        {/* Main Routes */}
        <Route exact path="/home" component={Home} />
        <Route exact path="/lessons" component={LessonsList} />
        <Route path="/lessons/:id" component={LessonDetail} />
        <Route exact path="/challenges" component={Challenges} />
        <Route path="/challenges/:id" component={ChallengeDetail} />
        <Route exact path="/playground" component={CodePlayground} />
        <Route exact path="/interviews" component={InterviewQuestions} />
        <Route exact path="/achievements" component={Achievements} />
        <Route exact path="/profile" component={Profile} />
        <Route exact path="/settings" component={Settings} />
        <Route exact path="/about" component={About} />
        <Route exact path="/help" component={Help} />
        <Route exact path="/privacy-policy" component={PrivacyPolicy} />
        <Route exact path="/terms-of-service" component={TermsOfService} />
        <Route exact path="/illustration-showcase" component={IllustrationShowcase} />
        
        {/* Default redirect */}
        <Route exact path="/">
          <Redirect to="/home" />
        </Route>
      </IonRouterOutlet>

      {/* Tab Bar */}
      <IonTabBar slot="bottom">
        <IonTabButton tab="home" href="/home" selected={isTabSelected('/home')}>
          <IonIcon icon={homeOutline} />
          <IonLabel>Home</IonLabel>
        </IonTabButton>
        
        <IonTabButton tab="lessons" href="/lessons" selected={isTabSelected('/lessons')}>
          <IonIcon icon={bookOutline} />
          <IonLabel>Learn</IonLabel>
        </IonTabButton>
        
        <IonTabButton tab="challenges" href="/challenges" selected={isTabSelected('/challenges')}>
          <IonIcon icon={codeSlashOutline} />
          <IonLabel>Practice</IonLabel>
        </IonTabButton>
        
        <IonTabButton tab="playground" href="/playground" selected={isTabSelected('/playground')}>
          <IonIcon icon={playOutline} />
          <IonLabel>Code</IonLabel>
        </IonTabButton>
        
        <IonTabButton tab="profile" href="/profile" selected={isTabSelected('/profile')}>
          <IonIcon icon={personOutline} />
          <IonLabel>Profile</IonLabel>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  );
}

export default App;